import { useState } from "react";

import { Grid } from "@mui/material";
import { Emptyable } from "@simplyk/common";

import { StrongAuthModalSubmitData } from "../../../common/contexts/StrongAuthContext";
import { ContactSupportLink } from "../ContactSupportLink";

import { AdminBypassContent } from "./AdminBypassContent/AdminBypassContent";
import { GoogleSsoContent } from "./GoogleSooContent/GoogleSsoContent";
import { PasswordContent } from "./PasswordContent/PasswordContent";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export interface StrongAuthModalProps {
  open: boolean;
  onSubmit: (data: StrongAuthModalSubmitData) => Promise<{ error: { code: string; message?: string } } | null>;
  onCancel: () => void;
}

const PASSWORD_MODAL_Z_INDEX = 999999999;

export const StrongAuthModal = ({ open, onSubmit, onCancel }: StrongAuthModalProps) => {
  const { t } = useTranslate();
  const { currentUser, connectedAsAdministrator } = useCurrentUserContext();
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Emptyable<string>>(undefined);
  const [askForBypassMfa, setAskForBypassMfa] = useState(false);

  const isGoogleSSO = currentUser?.isAuthenticatedWithGoogleSSO;

  const handleCancel = () => {
    setPassword("");
    setError(undefined);
    setAskForBypassMfa(false);
    onCancel();
  };

  const handleAdminBypass = async () => {
    setIsSubmitting(true);
    setError(undefined);

    const result = await onSubmit({ askForBypassMfa: true });
    if (result?.error) {
      setError(result.error.message || result.error.code);
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardDialog
      open={open}
      onClose={handleCancel}
      title={isGoogleSSO ? t("common", "googleSso.title") : t("common", "password.title")}
      maxWidth="sm"
      hideCloseButton
      sx={{ zIndex: PASSWORD_MODAL_Z_INDEX }}
    >
      <Grid container spacing={3}>
        <Grid item container spacing={2}>
          {askForBypassMfa ? (
            <AdminBypassContent isSubmitting={isSubmitting} error={error} onSubmit={handleAdminBypass} />
          ) : isGoogleSSO ? (
            <GoogleSsoContent
              isSubmitting={isSubmitting}
              error={error}
              onSubmit={onSubmit}
              setIsSubmitting={setIsSubmitting}
              setError={setError}
            />
          ) : (
            <PasswordContent
              password={password}
              isSubmitting={isSubmitting}
              error={error}
              onSubmit={onSubmit}
              setPassword={setPassword}
              setIsSubmitting={setIsSubmitting}
              setError={setError}
            />
          )}
        </Grid>

        <Grid item container spacing={2} sx={{ paddingBottom: 3 }}>
          {connectedAsAdministrator && (
            <Grid item container>
              <DashboardCheckbox
                data-test="admin-bypass-mfa-checkbox"
                checked={askForBypassMfa}
                onChange={(_event, checked) => setAskForBypassMfa(checked)}
                label={t("dashboard", "twoFactor.adminBypass.label")}
              />
            </Grid>
          )}
          <Grid item container justifyContent="center">
            <Grid item>
              <DashboardTypography variant="body2" marginRight={0.5}>
                {t("common", "password.help")}
              </DashboardTypography>
            </Grid>
            <Grid item>
              <ContactSupportLink />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </DashboardDialog>
  );
};
